/* eslint-disable no-await-in-loop */
import {
  ArchivedState,
  CheckForCompanySlugQuery,
  Company,
  CompanyAddressType,
  CompanyNameShowToFN,
  CompanyOnboardingSections,
  CompanyType,
  CreateAttorneyAssignmentMutation,
  CreateCompanyDataMutation,
  CreateCompanyInput,
  CreateCompanyMutation,
  CreateEmployerAdminAssignmentMutation,
  EmployerAssignment,
  CreateParalegalAssignmentMutation,
  FileType,
  GetCompanyAttorneyAssignmentQuery,
  GetCompanyEmployerAdminAssignmentQuery,
  GetCompanyForProfileQuery,
  GetCompanyParalegalAssignmentQuery,
  GetCompanyQuery,
  GetEmployerCompaniesQuery,
  JobDescription,
  listCompaniesQuery,
  ListCompanyAllEmployersQuery,
  ListCompanyJobDescriptionsQuery,
  Report,
  SearchableCompanyFilterInput,
  SearchCompaniesQuery,
  SearchCompanyCasesQuery,
  SearchCompanyDomainsQuery,
  SearchCompanyNamesQuery,
  UpdateCompanyInput,
  UpdateCompanyMutation,
} from 'API';
import { getCompany } from 'graphql/queries';
import * as mutations from 'graphql/mutations';
import {
  checkForCompanySlug,
  customSearchCompanies,
  getAttorneyAssignment,
  getEmployerAdminAssignment,
  getEmployerCompanies,
  getParalegalAssignment,
  listCompanies,
  listCompanyAllEmployers,
  listCompanyJobDescriptions,
  searchCompanyCases,
  searchCompanyDomains,
  searchCompanyNames,
} from 'graphql/customQueries';
import { nanoid } from 'nanoid';
import slugify from 'slugify';
import ActivityService from 'services/activity';
import FirmService, { FIRM_SLUG } from 'services/firm';
import PersonService from 'services/person';
import labels from 'shared/labels/companyLabels';
import { sleep, SLUGIFY_OPTIONS } from 'shared/constants';
import { ROUTES } from 'shared/routes';
import {
  alphaByTitle, getPayload, lower, notNullFilter,
} from 'shared/utilities';
import { Dispatch } from 'react';
import { getCompanyForProfile } from 'graphql/companyProfileQueries';
import { Link } from 'react-router-dom';
import { ActivityType } from 'models';
import { CompanyCase, PersonType } from 'shared/propTypes';
import { client } from 'lib/graphql-client';
import { GraphQLResult } from '@aws-amplify/api';
import { post } from 'aws-amplify/api';

const companyFileRequirements = [
  {
    type: FileType.COMPANY_FINANCIAL_STATEMENT,
    assignedToID: null,
    expirationDateRequired: false,
    isApproved: false,
  },
  {
    type: FileType.MARKETING_MATERIAL,
    assignedToID: null,
    expirationDateRequired: false,
    isApproved: false,
  },
  {
    type: FileType.PERM_SIGNATURE_PAGES,
    assignedToID: null,
    expirationDateRequired: false,
    isApproved: false,
  },
  {
    type: FileType.MASTER_AGREEMENT,
    assignedToID: null,
    expirationDateRequired: false,
    isApproved: false,
  },
  {
    type: FileType.VENDOR_CLIENT_LETTERS,
    assignedToID: null,
    expirationDateRequired: false,
    isApproved: false,
  },
  {
    type: FileType.OTHER,
    assignedToID: null,
    expirationDateRequired: false,
    isApproved: false,
  },
  {
    type: FileType.ACTUAL_PREVAILING_WAGE_MEMO,
    assignedToID: null,
    expirationDateRequired: false,
    isApproved: false,
  },
  {
    type: FileType.CERTIFIED_LABOR_CONDITION_APPLICATION,
    assignedToID: null,
    expirationDateRequired: true,
    isApproved: false,
  },
  {
    type: FileType.LABOR_CONDITION_APPLICATION_NOTICE_FILLING,
    assignedToID: null,
    expirationDateRequired: false,
    isApproved: false,
  },
  {
    type: FileType.OES_WAGES,
    assignedToID: null,
    expirationDateRequired: false,
    isApproved: false,
  },
  {
    type: FileType.PAF_TEMPLATES,
    assignedToID: null,
    expirationDateRequired: false,
    isApproved: false,
  },
  {
    type: FileType.SIGNED_CERTIFIED_LCA,
    assignedToID: null,
    expirationDateRequired: true,
    isApproved: false,
  },
  {
    type: FileType.SIGNED_NOTICE_FILING_CONFIRMATION,
    assignedToID: null,
    expirationDateRequired: false,
    isApproved: false,
  },
  {
    type: FileType.SUMMARY_OF_BENEFITS,
    assignedToID: null,
    expirationDateRequired: false,
    isApproved: false,
  },
  {
    type: FileType.EMPLOYMENT_VISA_CONSULAR_SIGNATURE_PAGES,
    assignedToID: null,
    expirationDateRequired: false,
    isApproved: false,
  },
  {
    type: FileType.EMPLOYMENT_VISA_USCIS_SIGNATURE_PAGES,
    assignedToID: null,
    expirationDateRequired: false,
    isApproved: false,
  },
  {
    type: FileType.PERM_SIGNATURE_PAGES,
    assignedToID: null,
    expirationDateRequired: false,
    isApproved: false,
  },
  {
    type: FileType.OTHER_PUBLIC_ACCESS_FILE,
    assignedToID: null,
    expirationDateRequired: false,
    isApproved: false,
  },
  // Extra Documents
  {
    type: FileType.H_1B_SIGNATURE_PAGES,
    assignedToID: null,
    expirationDateRequired: false,
    isApproved: false,
  },
  {
    type: FileType.H_1B1_SIGNATURE_PAGES,
    assignedToID: null,
    expirationDateRequired: false,
    isApproved: false,
  },
  {
    type: FileType.H_4_SIGNATURE_PAGES,
    assignedToID: null,
    expirationDateRequired: false,
    isApproved: false,
  },
  {
    type: FileType.ATTORNEY_CLIENT_AGREEMENT,
    assignedToID: null,
    expirationDateRequired: true,
    isApproved: false,
    issueDateRequired: true,
  },
];

class CompanyService {
  assignAttorney = async (companyID: string, attorneyID: string) => {
    const personID = PersonService.getPersonID();
    const personType = PersonService.getPersonType();

    try {
      const existing = (await client.graphql({
        query: getAttorneyAssignment,
        variables: {
          companyID,
          attorneyID,
        },
      })) as GraphQLResult<GetCompanyAttorneyAssignmentQuery>;

      if (
        existing?.data?.getCompany?.assignedAttorneys?.items
        && existing?.data?.getCompany?.assignedAttorneys?.items?.length > 0
      ) {
        return null;
      }

      const response = (await client.graphql({
        query: mutations.createAttorneyAssignment,
        variables: {
          input: {
            companyID,
            attorneyID,
          },
        },
      })) as GraphQLResult<CreateAttorneyAssignmentMutation>;

      if (response?.data?.createAttorneyAssignment) {
        ActivityService.createActivity({
          activityType: ActivityType.LEGAL_TEAM_ATTORNEY_ADDED,
          actorID: personID,
          actorType: personType,
          directTargetID: attorneyID,
          indirectTargetID: companyID,
        }, [
          attorneyID,
        ]);
      }

      return response?.data?.createAttorneyAssignment;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);

      return null;
    }
  };

  updateEmployerAssignments = async (
    employerID: string,
    newCompanyIDs: Array<string>,
    deletedAssignmentIDs: Array<string>,
  ) => {
    const body = {
      employerID,
      newCompanyIDs,
      deletedAssignmentIDs,
    };

    const payload = await getPayload(body);

    const { body: assignBody } = await post({ apiName: 'utilitiesapi', path: '/employerAssignment', options: payload as any }).response;

    return assignBody.json() as unknown as EmployerAssignment;
  };

  assignEmployerAdmin = async (employerID: string, companyID: string) => {
    try {
      const response = (await client.graphql({
        query: mutations.createEmployerAdminAssignment,
        variables: {
          input: {
            employerID,
            companyID,
          },
        },
      })) as GraphQLResult<CreateEmployerAdminAssignmentMutation>;

      return response.data?.createEmployerAdminAssignment;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);

      return null;
    }
  };

  /**
   * Delete an employerAdminAssignment. If we know the assignment ID,
   * we can delete it directly. If we instead have an employerID and
   * a companyID, we first have to fetch all of the company's admin
   * assignments that match the employerID. Most likely this is one, but
   * just in case we will iterate over the results and delete each one.
   * @param employerOrAssignmentID - an employerID or
   *                                 employerAdminAssignment ID
   * @param companyID - a company ID (present only if first argument is
   *                    employer)
   */
  unassignEmployerAdmin = async (
    employerOrAssignmentID: string,
    companyID?: string,
  ) => {
    let assignmentIDs: Array<string> = [];

    if (!companyID) {
      assignmentIDs = [employerOrAssignmentID];
    } else {
      const response = (await client.graphql({
        query: getEmployerAdminAssignment,
        variables: {
          companyID,
          employerID: employerOrAssignmentID,
        },
      })) as GraphQLResult<GetCompanyEmployerAdminAssignmentQuery>;

      assignmentIDs = response.data?.getCompany?.assignedEmployerAdmins?.items
        ?.filter(notNullFilter)
        .map((item) => item!.id) || [];

      // No admin assignments were found matching this combination
      // of companyID and employerID
      if (assignmentIDs.length === 0) {
        throw new Error();
      }
    }

    try {
      await Promise.all(
        assignmentIDs.map(async (assignmentID) => {
          await client.graphql({
            query: mutations.deleteEmployerAdminAssignment,
            variables: {
              input: {
                id: assignmentID,
              },
            },
          });
        }),
      );
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);

      throw new Error();
    }
  };

  assignParalegal = async (companyID: string, paralegalID: string) => {
    const personID = PersonService.getPersonID();
    const personType = PersonService.getPersonType();
    try {
      const existing = (await client.graphql({
        query: getParalegalAssignment,
        variables: {
          companyID,
          paralegalID,
        },
      })) as GraphQLResult<GetCompanyParalegalAssignmentQuery>;

      if (
        existing?.data?.getCompany?.assignedParalegals?.items
        && existing?.data?.getCompany?.assignedParalegals?.items?.length > 0
      ) {
        return null;
      }

      const response = (await client.graphql({
        query: mutations.createParalegalAssignment,
        variables: {
          input: {
            companyID,
            paralegalID,
          },
        },
      })) as GraphQLResult<CreateParalegalAssignmentMutation>;

      if (response?.data?.createParalegalAssignment) {
        ActivityService.createActivity({
          activityType: ActivityType.LEGAL_TEAM_PARALEGAL_ADDED,
          actorID: personID,
          actorType: personType,
          directTargetID: paralegalID,
          indirectTargetID: companyID,
        }, [
          paralegalID,
        ]);
      }

      return response?.data?.createParalegalAssignment;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);

      return null;
    }
  };

  assignParent = async (childCompanyID: string, parentCompanyID: string) => {
    try {
      const child = (await client.graphql({
        query: getCompany,
        variables: {
          id: childCompanyID,
        },
      })) as GraphQLResult<GetCompanyQuery>;

      // If this child already has a parent, quit.
      if (child.data?.getCompany?.parentID) {
        return null;
      }

      const response = (await client.graphql({
        query: mutations.updateCompany,
        variables: {
          input: {
            id: childCompanyID,
            parentID: parentCompanyID,
          },
        },
      })) as GraphQLResult<UpdateCompanyMutation>;

      return response.data?.updateCompany;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);

      return null;
    }
  };

  create = async (input: CreateCompanyInput) => {
    try {
      const companyID = nanoid();
      const newDataResponse = (await client.graphql({
        query: mutations.createCompanyData,
        variables: {
          input: {
            companyID,
            companyNameShowToFN: CompanyNameShowToFN.LEGAL_NAME_OF_US_COMPANY,
            requiresMeetingReminder: true,
            addressPrimary: {
              address: null,
              adminArea: null,
              careOf: null,
              city: null,
              companyAddressType: CompanyAddressType.HEADQUARTERS,
              country: null,
              postalCode: null,
              state: null,
              title: null,
              unitNumber: null,
              unitType: null,
            },
            addressSecondary: {
              address: null,
              adminArea: null,
              careOf: null,
              city: null,
              companyAddressType: CompanyAddressType.SECONDARY,
              country: null,
              postalCode: null,
              state: null,
              title: null,
              unitNumber: null,
              unitType: null,
            },
          },
        },
      })) as GraphQLResult<CreateCompanyDataMutation>;

      const companyDataId = newDataResponse.data?.createCompanyData?.id;

      const companyNumber = await FirmService.incrementCompanyCount();

      if (companyDataId) {
        const response = (await client.graphql({
          query: mutations.createCompany,
          variables: {
            input: {
              ...input,
              id: companyID,
              lowerName: lower(input?.displayCompanyName || input.name),
              lowerNameKey: lower(input?.displayCompanyName || input.name),
              displayCompanyName: input?.displayCompanyName || input.name,
              companyDataId,
              accountManagerID: input.accountManagerID,
              type: CompanyType.Company,
              archived: ArchivedState.ACTIVE,
              fileRequirements: companyFileRequirements,
              companyNumber,
            },
          },
        })) as GraphQLResult<CreateCompanyMutation>;

        return response.data?.createCompany;
      } throw new Error();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);

      return null;
    }
  };

  get: Get<Company> = async (id) => {
    try {
      const response = (await client.graphql({
        query: getCompany,
        variables: { id },
      })) as GraphQLResult<GetCompanyQuery>;

      if (response.data?.getCompany) {
        return response.data.getCompany as Company;
      }
      throw new Error();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);

      throw new Error();
    }
  };

  getJobDescriptions = async (id: string): Promise<Array<JobDescription>> => {
    try {
      const response = (await client.graphql({
        query: listCompanyJobDescriptions,
        variables: {
          id,
        },
      })) as GraphQLResult<ListCompanyJobDescriptionsQuery>;

      const descriptions = response?.data?.getCompany?.jobDescriptions;

      let jobsList = (descriptions?.items?.filter(notNullFilter)
        || []) as Array<JobDescription>;

      jobsList = jobsList.sort(alphaByTitle);

      return jobsList;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);

      throw new Error();
    }
  };

  getPrimaryContactIDs = (
    company?: Company,
  ):Array<string> => {
    if (!company) {
      return [];
    }

    return (company.primaryContacts?.items || [])
      .filter((item) => item?.employerID)
      .map((item) => item!.employerID) as Array<string>;
  };

  /**
   * Recursively generates unique slug for a company
   * by checking for a slugified version of a name.
   * If slug already exists, tries again with '-2'
   * appended to name, then '-3', '-4', etc.
   * @param {string} base
   * @param {number} counter
   * @returns {string}
   */
  generateSlug = async (base: string, counter: number = 1): Promise<string> => {
    let isTaken = false;

    const slug = slugify(
      `${base}${counter > 1 ? ` ${counter}` : ''}`,
      SLUGIFY_OPTIONS,
    );

    try {
      const response = (await client.graphql({
        query: checkForCompanySlug,
        variables: { slug },
      })) as GraphQLResult<CheckForCompanySlugQuery>;
      isTaken = response.data?.companiesBySlug?.items
        ? response.data.companiesBySlug.items.length > 0
        : false;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
    }

    if (slug === FIRM_SLUG) {
      isTaken = true;
    }

    return isTaken ? this.generateSlug(base, counter + 1) : slug;
  };

  getLabel = (fieldName: string, route?: string): string => {
    const questionnaireLabel = labels[fieldName] ? labels[fieldName].split(':') : '';

    if (route === 'questionnaire') {
      return questionnaireLabel[1];
    }

    if (route === 'company') {
      return questionnaireLabel[0];
    }

    return '';
  };

  link = (company: Company): string => `${ROUTES.companies}/${company.slug}`;

  linkName = (company: Company) => (
    <Link
      to={this.link(company)}
    >
      {company.name}
    </Link>
  );

  linkReport = (report: Report): string => `${ROUTES.reports}/${report.id}`;

  linkReports = (report: Report) => (
    <Link
      to={this.linkReport(report)}
    >
      {report.name}
    </Link>
  );

  initials = (company: Company): string => {
    if (!company.name) {
      return '-';
    }

    const cleanName = `${company.name}' '`
      .toLowerCase()
      .replace(/[^\w\s]|_/g, '') // Remove non alphanumeric characters
      .replace(' inc ', ' ') // Remove suffixes
      .replace(' ltd ', ' ')
      .replace(' and ', ' ')
      .replace(' co ', ' ')
      .replace(' llc ', ' ')
      .replace(' corp ', ' ');

    return cleanName
      .split(' ')
      .map((word) => word.slice(0, 1).toUpperCase())
      .join('');
  };

  searchByDomain: SearchByDomain = async (domain) => {
    if (!domain) {
      return [];
    }

    try {
      const response = (await client.graphql({
        query: searchCompanyDomains,
        variables: {
          domain,
        },
      })) as GraphQLResult<SearchCompanyDomainsQuery>;

      return (response.data?.searchCompanies?.items || []) as Array<Company>;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);
      throw new Error();
    }
  };

  searchByName: SearchByName<Company> = async (
    name?: string,
    maxCount?: number,
    archived?: ArchivedState,
  ) => {
    if (!name) {
      return [];
    }

    const chunks = lower(name).split(' ');

    const andArray: Array<SearchableCompanyFilterInput> = [
      { isPartnerCompany: { ne: true } },
      { archived: { eq: archived || 'ACTIVE' } },
    ];

    chunks.forEach((chunk) => {
      // Match either field. displayCompanyName is optional — 13 active companies
      // have none — and for another 44 it differs from the legal name (e.g.
      // "Hyper Labs, Inc." displays as "Hyperscience"), so searching only the
      // display name leaves those companies unfindable.
      andArray.push({
        or: [
          { displayCompanyName: { wildcard: `*${chunk}*` } },
          { name: { wildcard: `*${chunk}*` } },
        ],
      });
    });

    try {
      const response = (await client.graphql({
        query: searchCompanyNames,
        variables: {
          and: andArray,
          limit: maxCount,
        },
      })) as GraphQLResult<SearchCompanyNamesQuery>;

      return (response.data?.searchCompanies?.items
        || []) as Array<Company>;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);
      throw new Error();
    }
  };

  unassignAttorney = async (relationshipID: string) => {
    try {
      const response = await client.graphql({
        query: mutations.deleteAttorneyAssignment,
        variables: {
          input: { id: relationshipID },
        },
      });

      return response;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);
      throw new Error();
    }
  };

  unassignParalegal = async (relationshipID: string) => {
    try {
      const response = await client.graphql({
        query: mutations.deleteParalegalAssignment,
        variables: {
          input: { id: relationshipID },
        },
      });

      return response;
    } catch {
      throw new Error();
    }
  };

  unassignParent = async (childCompanyID: string) => {
    try {
      const response = await client.graphql({
        query: mutations.updateCompany,
        variables: {
          input: {
            id: childCompanyID,
            parentID: null,
          },
        },
      });

      return response;
    } catch {
      throw new Error();
    }
  };

  update = async (
    input: UpdateCompanyInput,
    setCompany?: Dispatch<Company>,
  ) => {
    try {
      const response = (await client.graphql({
        query: mutations.updateCompany,
        variables: {
          input,
        },
      })) as GraphQLResult<UpdateCompanyMutation>;

      if (setCompany) {
        const companyResponse = (await client.graphql({
          query: getCompanyForProfile,
          variables: { id: input.id },
        })) as GraphQLResult<GetCompanyForProfileQuery>;

        setCompany((companyResponse?.data?.getCompany as Company) || null);
      }

      return response.data?.updateCompany;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);

      return null;
    }
  };

  /**
   * Determines whether completed onboarding has
   */
  hasCompletedOnboarding = (
    onboardingSections?: CompanyOnboardingSections,
  ): Boolean => {
    let onBoarding : CompanyOnboardingSections = {
      __typename: 'CompanyOnboardingSections',
      companyInfo: false,
      contactInfo: false,
      companyPreferences: false,
      immigrationPreferences: false,
      paymentPreferences: false,
      questions: false,
      employeeInformation: false,
    };
    if (onboardingSections) {
      onBoarding = onboardingSections;
    }
    const values = Object.values(onBoarding);

    return !values.some((value) => !value);
  };

  /**
   * Onboarding sections done of company service
   */
  onboardingSectionsDone = (
    onboardingSections?: CompanyOnboardingSections,
  ): [number, number] => {
    if (!onboardingSections) {
      return [0, 0];
    }

    const values = Object.values(onboardingSections);

    return [values.filter((value) => value).length, values.length];
  };

  /* Get all Active Companies */

  getActiveCompanies = async (isMH: boolean, employerID: string) => {
    const companiesArray: Company[] = [];
    let nextToken = null;
    const limit = 300;
    try {
      if (isMH) {
        do {
          // eslint-disable-next-line no-await-in-loop
          const response = await (client.graphql({
            query: customSearchCompanies,
            variables: {
              filter: { archived: { eq: ArchivedState.ACTIVE } },
              limit,
              nextToken,
            },
          })) as GraphQLResult<SearchCompaniesQuery>;

          await sleep(2000);

          if (response?.data?.searchCompanies) {
            const { items } = response?.data?.searchCompanies;
            nextToken = response?.data?.searchCompanies?.nextToken;

            companiesArray.push(...(items as Company[] || []));
          }
        } while (nextToken);
      } else if (employerID) {
        const emCompanies = await this.activeEmCompanies(employerID);
        companiesArray.push(...(emCompanies || []));
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('ERROR >>>>>', error);
    }

    return companiesArray;
  };

  // Get All Active EM Companies
  activeEmCompanies = async (id:string) => {
    let nextToken = null;
    let allCompanies: Array<Company> = [];
    try {
      if (!id) return allCompanies;
      do {
        // eslint-disable-next-line no-await-in-loop
        const fetchedCompanies = await client.graphql({
          query: getEmployerCompanies,
          variables: { id, nextToken },
        }) as GraphQLResult<GetEmployerCompaniesQuery>;
        const { items, nextToken: token } = fetchedCompanies?.data?.getEmployer?.companies || {};
        if (items?.length && !!items[0]?.company) {
          const emCompanies: Company[] = [];
          items?.map((item) => emCompanies.push(item?.company as Company));
          if (emCompanies && emCompanies?.length) {
            allCompanies = [...allCompanies, ...emCompanies] as Array<Company>;
          }
        }
        nextToken = token;
      } while (nextToken !== null);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error while fetching companies: ', error);
    }

    return allCompanies;
  };

  getCompanySponsors = async (companies: Array<Company>) => {
    const companySponsors: PersonType[] = [];
    const nextToken = null;
    const limit = 1000;
    const orCondition: any = [];

    companies.map((company: Company) => {
      orCondition.push({ id: { eq: company.id } });

      return company.id;
    });
    const filter = { or: orCondition };

    try {
      do {
        // eslint-disable-next-line no-await-in-loop
        const response = await (client.graphql({
          query: searchCompanyCases,
          variables: {
            filter,
            limit,
            nextToken,
          },
        })) as GraphQLResult<SearchCompanyCasesQuery>;

        const fetchedCompanies = response?.data?.searchCompanies?.items || null;

        if (fetchedCompanies?.length) {
          fetchedCompanies.forEach((fetchedCompany) => {
            const { items: companyCases } = fetchedCompany?.cases || {};

            if (companyCases?.length) {
              companyCases.forEach((companyCase: CompanyCase) => {
                const { sponsoringEmployer } = companyCase || {};

                if (sponsoringEmployer) {
                  if (companySponsors?.length) {
                    const isExist = companySponsors.filter(
                      (person: PersonType) => person.id === sponsoringEmployer.id,
                    );
                    if (!isExist?.length) companySponsors.push(sponsoringEmployer as PersonType);
                  } else companySponsors.push(sponsoringEmployer as PersonType);
                }
              });
            }
          });
        }
      } while (nextToken);

      return companySponsors;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Something went wrong, Error: ', error);

      return null;
    }
  };

  getCompanyContributors = async (companies: Array<Company>) => {
    const companyContributors: PersonType[] = [];
    const nextToken = null;
    const limit = 1000;
    const orCondition: any = [];

    companies.map((company: Company) => {
      orCondition.push({ id: { eq: company.id } });

      return company.id;
    });
    const filter = { or: orCondition };

    try {
      do {
        // eslint-disable-next-line no-await-in-loop
        const response = await (client.graphql({
          query: searchCompanyCases,
          variables: {
            filter,
            limit,
            nextToken,
          },
        })) as GraphQLResult<SearchCompanyCasesQuery>;

        const fetchedCompanies = response?.data?.searchCompanies?.items || null;

        if (fetchedCompanies?.length) {
          fetchedCompanies.forEach((fetchedCompany) => {
            const { items: companyCases } = fetchedCompany?.cases || {};

            if (companyCases?.length) {
              companyCases.forEach((companyCase: CompanyCase) => {
                const { items: caseContributors } = companyCase?.contributors || {};

                if (caseContributors?.length) {
                  caseContributors.forEach((contributor) => {
                    const { contributorEmployer, contributorTeamMember } = contributor || {};

                    if (contributorTeamMember) {
                      const isExist = companyContributors.filter(
                        (person: PersonType) => person.id === contributorTeamMember.id,
                      );
                      if (!isExist?.length) companyContributors.push(contributorTeamMember as PersonType);
                    } else if (contributorEmployer) {
                      const isExist = companyContributors.filter(
                        (person: PersonType) => person.id === contributorEmployer.id,
                      );
                      if (!isExist?.length) companyContributors.push(contributorEmployer as PersonType);
                    }
                  });
                }
              });
            }
          });
        }
      } while (nextToken);

      return companyContributors;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Something went wrong, Error: ', error);

      return null;
    }
  };

  getCompanyAttorneys = async (companies: Array<Company>) => {
    const attorneys: PersonType[] = [];
    const nextToken = null;
    const limit = 1000;
    const orCondition: any = [];

    companies.map((company: Company) => {
      orCondition.push({ id: { eq: company.id } });

      return company.id;
    });
    const filter = { or: orCondition };

    try {
      do {
        // eslint-disable-next-line no-await-in-loop
        const response = await (client.graphql({
          query: searchCompanyCases,
          variables: {
            filter,
            limit,
            nextToken,
          },
        })) as GraphQLResult<SearchCompanyCasesQuery>;

        const fetchedCompanies = response?.data?.searchCompanies?.items || null;

        if (fetchedCompanies?.length) {
          fetchedCompanies.forEach((fetchedCompany) => {
            const { items: companyCases } = fetchedCompany?.cases || {};

            if (companyCases?.length) {
              companyCases.forEach((companyCase: CompanyCase) => {
                const { attorney } = companyCase || {};

                if (attorney) {
                  if (attorneys?.length) {
                    const isExist = attorneys.filter(
                      (person: PersonType) => person.id === attorney.id,
                    );
                    if (!isExist?.length) attorneys.push(attorney as PersonType);
                  } else attorneys.push(attorney as PersonType);
                }
              });
            }
          });
        }
      } while (nextToken);

      return attorneys;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Something went wrong, Error: ', error);

      return null;
    }
  };

  getCompanyManagers = async (companies: Array<Company>) => {
    const managers: PersonType[] = [];
    const nextToken = null;
    const limit = 1000;
    const orCondition: any = [];

    companies.map((company: Company) => {
      orCondition.push({ id: { eq: company.id } });

      return company.id;
    });
    const filter = { or: orCondition };

    try {
      do {
        // eslint-disable-next-line no-await-in-loop
        const response = await (client.graphql({
          query: searchCompanyCases,
          variables: {
            filter,
            limit,
            nextToken,
          },
        })) as GraphQLResult<SearchCompanyCasesQuery>;

        const fetchedCompanies = response?.data?.searchCompanies?.items || null;

        if (fetchedCompanies?.length) {
          fetchedCompanies.forEach((fetchedCompany) => {
            const { items: companyCases } = fetchedCompany?.cases || {};

            if (companyCases?.length) {
              companyCases.forEach((companyCase: CompanyCase) => {
                const { caseManager } = companyCase || {};

                if (caseManager) {
                  if (managers?.length) {
                    const isExist = managers.filter(
                      (person: PersonType) => person.id === caseManager.id,
                    );
                    if (!isExist?.length) managers.push(caseManager as PersonType);
                  } else managers.push(caseManager as PersonType);
                }
              });
            }
          });
        }
      } while (nextToken);

      return managers;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Something went wrong, Error: ', error);

      return null;
    }
  };

  searchCompanyWithIds = async (companyIds: string[]) => {
    const companiesArray: Company[] = [];
    let nextToken = null;
    const limit = 300;
    const orCondition: any = [];

    companyIds.map((id: string) => {
      orCondition.push({ id: { eq: id } });

      return id;
    });
    const filter = { or: orCondition };

    try {
      do {
        // eslint-disable-next-line no-await-in-loop
        const response = await (client.graphql({
          query: customSearchCompanies,
          variables: {
            filter,
            limit,
            nextToken,
          },
        })) as GraphQLResult<SearchCompaniesQuery>;

        if (response?.data?.searchCompanies) {
          const { items } = response?.data?.searchCompanies;
          nextToken = response?.data?.searchCompanies?.nextToken;

          companiesArray.push(...(items as Company[] || []));
        }
      } while (nextToken);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('ERROR >>>>>>', error);
    }

    return companiesArray;
  };

  // Get All My Companies
  myCompanies = async (id:string) => {
    let nextToken = null;
    let allCompanies: Array<Company> = [];
    try {
      if (!id) return allCompanies;
      do {
        // eslint-disable-next-line no-await-in-loop
        const fetchedCompanies = await client.graphql({
          query: getEmployerCompanies,
          variables: { id, nextToken },
        }) as GraphQLResult<GetEmployerCompaniesQuery>;
        const { items, nextToken: token } = fetchedCompanies?.data?.getEmployer?.companies || {};
        if (items?.length && !!items[0]?.company) {
          const emCompanies: Company[] = [];
          items?.map((item) => (item?.company?.archived === 'ACTIVE' ? emCompanies.push(item?.company as Company) : false));
          if (emCompanies && emCompanies?.length) {
            allCompanies = [...allCompanies, ...emCompanies] as Array<Company>;
          }
        }
        nextToken = token;
      } while (nextToken !== null);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error while fetching companies: ', error);
    }

    return allCompanies;
  };

  // Get All Companies
  getAllCompanies = async (): Promise<Company[]> => {
    let nextToken = null;
    let allCompanies: Array<Company> = [];
    try {
      do {
        // eslint-disable-next-line no-await-in-loop
        const fetchedCompanies = await client.graphql({
          query: listCompanies,
          variables: { nextToken, limit: 100 },
        }) as GraphQLResult<listCompaniesQuery>;
        const { items, nextToken: token } = fetchedCompanies?.data?.listCompanies || {};
        if (items?.length) {
          allCompanies = [...allCompanies, ...items] as Array<Company>;
        }
        nextToken = token;
      } while (nextToken !== null);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error while fetching all companies: ', error);
    }

    return allCompanies;
  };

  // teammembers associated with company cases i.e, Attorneys + Managers + Contributors
  getCompanyTeamMembers = async (companies: Array<Company>) => {
    const attorneys: PersonType[] = [];
    const managers: PersonType[] = [];
    const companyContributors: PersonType[] = [];

    const nextToken = null;
    const limit = 1000;
    const orCondition: any = [];

    companies.map((company: Company) => {
      orCondition.push({ id: { eq: company.id } });

      return company.id;
    });
    const filter = { or: orCondition };

    try {
      do {
        // eslint-disable-next-line no-await-in-loop
        const response = await (client.graphql({
          query: searchCompanyCases,
          variables: {
            filter,
            limit,
            nextToken,
          },
        })) as GraphQLResult<SearchCompanyCasesQuery>;

        const fetchedCompanies = response?.data?.searchCompanies?.items || null;

        if (fetchedCompanies?.length) {
          fetchedCompanies.forEach((fetchedCompany) => {
            const { items: companyCases } = fetchedCompany?.cases || {};

            if (companyCases?.length) {
              companyCases.forEach((companyCase: CompanyCase) => {
                const { items: caseContributors } = companyCase?.contributors || {};
                const { attorney: caseAttorney, caseManager } = companyCase || {};

                if (caseAttorney) {
                  const isExist = attorneys?.length
                    ? attorneys.filter((attorney) => attorney.id === caseAttorney.id)
                    : [];
                  if (!isExist?.length) attorneys.push({ ...caseAttorney } as PersonType);
                }

                if (caseManager) {
                  const isExist = managers?.length
                    ? managers.filter((manager) => manager.id === caseManager.id)
                    : [];
                  if (!isExist?.length) {
                    managers.push({ ...caseManager } as PersonType);
                  }
                }
                // adding contributors
                if (caseContributors?.length) {
                  caseContributors.forEach((contributor) => {
                    const { contributorEmployer, contributorTeamMember } = contributor || {};

                    if (contributorTeamMember) {
                      const isExist = companyContributors.filter(
                        (person: PersonType) => person.id === contributorTeamMember.id,
                      );
                      if (!isExist?.length) companyContributors.push(contributorTeamMember as PersonType);
                    } else if (contributorEmployer) {
                      const isExist = companyContributors.filter(
                        (person: PersonType) => person.id === contributorEmployer.id,
                      );
                      if (!isExist?.length) companyContributors.push(contributorEmployer as PersonType);
                    }
                  });
                }
              });
            }
          });
        }
      } while (nextToken);

      return [...attorneys, ...managers, ...companyContributors];
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Something went wrong, Error: ', error);

      return null;
    }
  };

  getCompanyEmployersForEM = async (companyIDs: string[]) => {
    if (companyIDs?.length) {
      const promises = companyIDs.map(async (companyId) => {
        const companyResponse = (await client.graphql({
          query: getCompanyForProfile,
          variables: { id: companyId },
        })) as GraphQLResult<GetCompanyForProfileQuery>;

        const { assignedEmployers } = companyResponse?.data?.getCompany || {};
        const { items: employersData } = assignedEmployers || {};
        const companyEmployers: PersonType[] = employersData?.length
          ? employersData.map((employerData) => employerData?.employer) as unknown as PersonType[]
          : [];

        return companyEmployers;
      });

      const results = await Promise.all(promises);
      if (results?.length) {
        const companyEmployers = results[0];

        return companyEmployers;
      }

      return [];
    }

    return [];
  };

  getCompanyIndividuals = async (companies: Array<Company>, isMH: boolean) => {
    let persons: string[] = [];
    let employers: PersonType[] = [];
    let companiesLegalTeam: string [] = [];
    let nextToken = null;
    const limit = 1000;
    const orCondition: any = [];

    companies.map((company: Company) => {
      orCondition.push({ id: { eq: company.id } });

      return company.id;
    });
    const filter = { or: orCondition };

    try {
      if (isMH) {
        do {
          // eslint-disable-next-line no-await-in-loop
          const response = await (client.graphql({
            query: listCompanyAllEmployers,
            variables: {
              filter,
              limit,
              nextToken,
            },
          })) as GraphQLResult<ListCompanyAllEmployersQuery>;

          const { items, nextToken: token } = response?.data?.listCompanies || {};
          if (items?.length) {
            // eslint-disable-next-line @typescript-eslint/no-loop-func
            items.forEach((item) => {
              const { assignedEmployers, legalTeamIDs } = item || {};
              const { items: employersData } = assignedEmployers || {};
              const companyEmployers: PersonType[] = employersData?.length
                ? employersData.map((employerData) => employerData?.employer) as PersonType[]
                : [];

              employers = [...employers, ...companyEmployers];
              if (legalTeamIDs?.length) {
                companiesLegalTeam = [...companiesLegalTeam, ...legalTeamIDs];
              }
            });
          }

          nextToken = token;
        } while (nextToken);
      } else {
        const companyIDs = companies?.length ? companies.map((company: Company) => company.id) : [];
        const companyEmployers: PersonType [] = await this.getCompanyEmployersForEM(companyIDs);

        if (companyEmployers?.length) {
          employers = [...employers, ...companyEmployers];
        }
      }

      const teammembers = await this.getCompanyTeamMembers(companies);
      if (employers?.length) {
        persons = employers.map((employer: PersonType) => employer.id);
      }

      if (teammembers?.length) {
        persons = [...persons, ...teammembers.map((teammember: PersonType) => teammember.id)];
      }

      if (companiesLegalTeam?.length) {
        persons = [...persons, ...companiesLegalTeam];
      }

      const uniqueIds = persons.filter((person, index) => persons.indexOf(person) === index);

      return uniqueIds;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Something went wrong, Error: ', error);

      return null;
    }
  };
}

export default new CompanyService();
